// src/api/metaWhatsappApi.js
// RTK Query slice for Meta WhatsApp V2 (/api/v3/meta-whatsapp).
//
// ★ CAPABILITY LISTS COME FROM THE SERVER, NEVER FROM THE CLIENT PERMISSION MAP.
// The permission snapshot in Redux is captured at login and is stale until a
// reload — a channel assigned five minutes ago is invisible to it. Every list of
// "what may I do" (channels, eligible senders) is therefore a query, and the
// server re-checks on every call regardless.
//
// Tags are declared so a mutation invalidates the lists it affects; this
// codebase's older slices omit `invalidatesTags` and hand-patch the cache
// instead, which is why stale inboxes are a recurring complaint.

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { constant } from 'constant';

export const META_WHATSAPP_BASE = `${constant.baseUrl}api/v3/meta-whatsapp`;

/**
 * The headers every call to this API carries.
 *
 * Exported because media is fetched OUTSIDE RTK Query — a photo has to become
 * an object URL, not a cache entry — and that fetch must authenticate exactly
 * the way the slice does, or the two drift apart the first time a header
 * changes.
 */
export const authHeaders = () => {
	const headers = {};
	const token =
		localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
	if (token) headers.Authorization = `Bearer ${token}`;

	// The tenant travels on every request; the server treats it as a hint and
	// derives the real tenant from the token.
	const tenantId =
		localStorage.getItem('tenantId') || sessionStorage.getItem('tenantId');
	if (tenantId) headers['x-tenant-id'] = tenantId;

	return headers;
};

const cleanParams = (params = {}) =>
	Object.fromEntries(
		Object.entries(params).filter(
			([, value]) =>
				value !== undefined &&
				value !== null &&
				value !== ''
		)
	);


/** Where a message's photo, voice note or document is served from. */
export const mediaUrl = (conversationId, wamid) =>
	`${META_WHATSAPP_BASE}/conversations/${encodeURIComponent(conversationId)}` +
	`/media/${encodeURIComponent(wamid)}`;

const baseQuery = fetchBaseQuery({
	baseUrl: META_WHATSAPP_BASE,
	prepareHeaders: (headers) => {
		for (const [name, value] of Object.entries(authHeaders())) headers.set(name, value);
		return headers;
	},
});

export const metaWhatsappApi = createApi({
	reducerPath: 'metaWhatsappApi',
	baseQuery,
	tagTypes: [
		'Channel',
		'Conversation',
		'Message',
		'Assignment',
		'Settings',
		// Campaign engine. `Campaign` is tagged per id AND as a LIST, because the
		// list screen and the dashboard are open at once often enough that a
		// pause on one must move the other.
		'Campaign',
		'Sender',
		'Template',
		'Suppression',
		'CampaignSettings',
	],
	endpoints: (builder) => ({
		// ─── Channels ──────────────────────────────────────────────────────
		listChannels: builder.query({
			query: () => '/channels',
			transformResponse: (r) => r?.channels ?? [],
			providesTags: ['Channel'],
		}),

		// Which channels this user may START a conversation from. Server-computed
		// — the client must not infer it from permissions.
		listEligibleChannels: builder.query({
			query: () => '/channels/eligible',
			transformResponse: (r) => r?.channels ?? [],
			providesTags: ['Channel'],
		}),

		// ─── Conversations ─────────────────────────────────────────────────
		listConversations: builder.query({
			query: (params = {}) => ({ url: '/conversations', params }),
			providesTags: (result) =>
				result?.conversations
					? [
							...result.conversations.map((c) => ({ type: 'Conversation', id: c._id })),
							{ type: 'Conversation', id: 'LIST' },
						]
					: [{ type: 'Conversation', id: 'LIST' }],
		}),

		getConversation: builder.query({
			query: (id) => `/conversations/${id}`,
			transformResponse: (r) => r?.conversation ?? null,
			providesTags: (result, error, id) => [{ type: 'Conversation', id }],
		}),

		listMessages: builder.query({
			query: ({ conversationId, ...params }) => ({
				url: `/conversations/${conversationId}/messages`,
				params,
			}),
			providesTags: (result, error, { conversationId }) => [
				{ type: 'Message', id: conversationId },
			],
		}),

		// ─── Sending ───────────────────────────────────────────────────────
		// ⚠ The body must NOT carry a sender field — the server answers 400
		// UNEXPECTED_SENDER_FIELD rather than silently overriding it, so a
		// component that tries to choose a number fails loudly.
		sendMessage: builder.mutation({
			// `localUrl` and `templatePreview` are the browser's own drawing of the
			// pending bubble — for the optimistic update only, never for the
			// server. The template's shape comes back on the recorded message; the
			// server builds what is sent from the mirror and refuses a body that
			// tries to supply its own components.
			query: ({ conversationId, localUrl, templatePreview, ...body }) => ({
				url: `/conversations/${conversationId}/messages`,
				method: 'POST',
				body,
			}),
			// The bubble appears the moment Enter is pressed, with a clock tick, and
			// is confirmed (or withdrawn) when the server answers — the thread never
			// freezes on a spinner while the Graph call is in flight. The list is
			// newest-first, so the pending message goes on the front.
			async onQueryStarted(
				{ conversationId, clientMessageId, type = 'text', text, context, media, localUrl, templatePreview },
				{ dispatch, queryFulfilled },
			) {
				const pending = {
					_id: clientMessageId,
					wamid: null,
					direction: 'outbound',
					type,
					content: text?.body ?? null,
					// ★ A TEMPLATE USED TO DRAW A BLANK BUBBLE. Only `text` was read
					// here, so pressing Send on a template put an empty green box in
					// the thread until the refetch replaced it. The picker already
					// holds the mirror row it is previewing, so it hands the same
					// definition and values over and the pending bubble renders
					// exactly as the recorded one will.
					template: templatePreview ?? undefined,
					// The quote and the attachment show on the pending bubble exactly
					// as they will once the server has recorded them. The connection
					// layer's field is `messageId`; the stored field is `wamid`.
					context: context?.messageId ? { wamid: context.messageId } : undefined,
					media: media
						? {
								assetId: media.id ?? null,
								mimeType: media.mimeType ?? null,
								filename: media.filename ?? null,
								size: media.size ?? null,
								caption: media.caption ?? null,
							}
						: undefined,
					localUrl: localUrl ?? null,
					status: 'PENDING',
					occurredAt: new Date().toISOString(),
				};
				const patch = dispatch(
					metaWhatsappApi.util.updateQueryData('listMessages', { conversationId }, (draft) => {
						if (!draft?.messages) return;
						// A retry reuses the same key; do not draw the bubble twice.
						if (draft.messages.some((m) => m._id === clientMessageId)) return;
						draft.messages.unshift(pending);
					}),
				);
				try {
					const { data } = await queryFulfilled;
					dispatch(
						metaWhatsappApi.util.updateQueryData('listMessages', { conversationId }, (draft) => {
							const sent = draft?.messages?.find((m) => m._id === clientMessageId);
							if (!sent) return;
							sent.wamid = data?.wamid ?? null;
							sent.status = data?.status ?? 'ACCEPTED';
						}),
					);
				} catch {
					// Refused or failed: the bubble goes away and the composer explains.
					patch.undo();
				}
			},
			invalidatesTags: (result, error, { conversationId }) => [
				{ type: 'Message', id: conversationId },
				{ type: 'Conversation', id: conversationId },
				{ type: 'Conversation', id: 'LIST' },
			],
		}),

		startConversation: builder.mutation({
			query: (body) => ({ url: '/conversations', method: 'POST', body }),
			// ★ THE LEAD'S OWN LIST TOO. The lead drawer reads `lead-<id>`; without
			// this it kept saying "no conversation yet" — with its Start button —
			// above the thread that had just been opened.
			invalidatesTags: (result, error, body) => [
				{ type: 'Conversation', id: 'LIST' },
				...(body?.leadId ? [{ type: 'Conversation', id: `lead-${body.leadId}` }] : []),
			],
		}),

		// Adopt a chat that already exists on the number a lead has today. The
		// server refuses anything that is not on that number, so this can be
		// offered from a prompt rather than reserved for an administrator.
		attachLeadConversation: builder.mutation({
			query: ({ conversationId, leadId }) => ({
				url: `/conversations/${conversationId}/attach-lead`,
				method: 'POST',
				body: { leadId },
			}),
			invalidatesTags: (result, error, { conversationId, leadId }) => [
				{ type: 'Conversation', id: conversationId },
				{ type: 'Conversation', id: `lead-${leadId}` },
				{ type: 'Conversation', id: 'LIST' },
				// The thread gains a note saying it was attached.
				{ type: 'Message', id: conversationId },
			],
		}),

		markRead: builder.mutation({
			query: (conversationId) => ({
				url: `/conversations/${conversationId}/read`,
				method: 'POST',
			}),
			invalidatesTags: (result, error, conversationId) => [
				{ type: 'Conversation', id: conversationId },
				{ type: 'Conversation', id: 'LIST' },
			],
		}),

		linkLead: builder.mutation({
			query: ({ conversationId, leadId }) => ({
				url: `/conversations/${conversationId}/lead`,
				method: 'PATCH',
				body: { leadId },
			}),
			invalidatesTags: (result, error, { conversationId }) => [
				{ type: 'Conversation', id: conversationId },
				{ type: 'Conversation', id: 'LIST' },
			],
		}),

		// ─── Canonical conversation ownership ──────────────────────────────
		// One external thread, one person holding the keyboard. Reading stays
		// open to everyone with a related lead; these decide who may reply.
		claimConversation: builder.mutation({
			query: (conversationId) => ({
				url: `/conversations/${conversationId}/claim`,
				method: 'POST',
			}),
			invalidatesTags: (result, error, conversationId) => [
				{ type: 'Conversation', id: conversationId },
				{ type: 'Conversation', id: 'LIST' },
			],
		}),

		// Who this conversation may be handed to — server-computed from the
		// channel's live assignments, never inferred from a client-side user list.
		listTransferCandidates: builder.query({
			query: (conversationId) => `/conversations/${conversationId}/transfer-candidates`,
			transformResponse: (r) => r?.candidates ?? [],
			providesTags: (result, error, conversationId) => [
				{ type: 'Conversation', id: `candidates-${conversationId}` },
				// ★ A COLLECTIVE TAG, BECAUSE ASSIGNMENT IS NOT PER CONVERSATION.
				// Candidates come from the number's roster, so removing somebody
				// from the number staled every open thread's list at once — and a
				// per-conversation tag gives an assignment change nothing it can
				// name. Without this the menu kept offering a revoked colleague
				// for up to a minute, and picking them answered 422.
				{ type: 'Conversation', id: 'CANDIDATES' },
			],
		}),

		transferConversation: builder.mutation({
			query: ({ conversationId, toUserId, expectedOwner }) => ({
				url: `/conversations/${conversationId}/transfer`,
				method: 'POST',
				// `expectedOwner` makes this a compare-and-swap server-side: if
				// ownership moved since this screen loaded, the transfer is
				// refused rather than overwriting whatever happened.
				body: { toUserId, expectedOwner },
			}),
			invalidatesTags: (result, error, { conversationId }) => [
				{ type: 'Conversation', id: conversationId },
				{ type: 'Conversation', id: 'LIST' },
				// The new owner is no longer a candidate.
				{ type: 'Conversation', id: `candidates-${conversationId}` },
			],
		}),

		releaseConversation: builder.mutation({
			query: (conversationId) => ({
				url: `/conversations/${conversationId}/release`,
				method: 'POST',
			}),
			invalidatesTags: (result, error, conversationId) => [
				{ type: 'Conversation', id: conversationId },
				{ type: 'Conversation', id: 'LIST' },
			],
		}),

		// ─── Lead detail tab ───────────────────────────────────────────────
		listLeadConversations: builder.query({
			query: (leadId) => `/leads/${leadId}/conversations`,
			// The lead's facts travel with its threads: `lead.hasContactNumber`
			// decides whether "Start a conversation" can be offered at all. Only
			// the drawer reads this hook.
			transformResponse: (r) => ({ conversations: r?.conversations ?? [], lead: r?.lead ?? null }),
			providesTags: (result, error, leadId) => [{ type: 'Conversation', id: `lead-${leadId}` }],
		}),

		// ─── Settings ──────────────────────────────────────────────────────
		listSettingsChannels: builder.query({
			query: () => '/settings/channels',
			providesTags: ['Settings'],
		}),

		patchChannel: builder.mutation({
			query: ({ phoneNumberId, ...body }) => ({
				url: `/settings/channels/${phoneNumberId}`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: ['Settings', 'Channel'],
		}),

		listAssignments: builder.query({
			query: (phoneNumberId) => `/settings/channels/${phoneNumberId}/assignments`,
			transformResponse: (r) => r?.assignments ?? [],
			providesTags: (result, error, phoneNumberId) => [
				{ type: 'Assignment', id: phoneNumberId },
			],
		}),

		assignUser: builder.mutation({
			query: ({ phoneNumberId, userId }) => ({
				url: `/settings/channels/${phoneNumberId}/assignments`,
				method: 'POST',
				body: { userId },
			}),
			invalidatesTags: (result, error, { phoneNumberId }) => [
				{ type: 'Assignment', id: phoneNumberId },
				'Settings',
				// Who may be handed a thread on this number just changed.
				{ type: 'Conversation', id: 'CANDIDATES' },
			],
		}),

		// ★ `releaseOwned` IS THE OTHER HALF OF A REASSIGNMENT.
		// Revoking a grant does not touch conversation ownership, so a number
		// handed from one agent to another leaves every open thread owned by the
		// person who left: the new assignee can read them and answer none. Passing
		// it returns those threads to the unclaimed pool in the same request.
		// Off by default — a revoke that silently rearranged ownership would be a
		// surprise, and someone changing teams should keep their conversations.
		revokeUser: builder.mutation({
			query: ({ phoneNumberId, userId, reason, releaseOwned = false }) => ({
				url: `/settings/channels/${phoneNumberId}/assignments/${userId}`,
				method: 'DELETE',
				body: { reason, releaseOwned },
			}),
			invalidatesTags: (result, error, { phoneNumberId }) => [
				{ type: 'Assignment', id: phoneNumberId },
				'Settings',
				// A revocation changes what the revoked user may see, so their
				// channel list is stale too.
				'Channel',
				// And they can no longer be handed a thread on this number — the
				// transfer menu must stop offering them before someone picks them.
				{ type: 'Conversation', id: 'CANDIDATES' },
				// Their threads may have been released with them.
				{ type: 'Conversation', id: 'LIST' },
			],
		}),

		getIntegration: builder.query({
			query: () => '/settings/integration',
			providesTags: ['Settings'],
		}),

		// The assignment collection IS the history — append-only, one row per
		// grant, revoked rows kept. `channel` narrows it to one number.
		listAudit: builder.query({
			query: (params = {}) => ({ url: '/settings/audit', params }),
			transformResponse: (r) => r?.entries ?? [],
			providesTags: ['Settings'],
		}),

		// ─── Connecting a number ───────────────────────────────────────
		// The setup read carries the tenant's webhook callback URL and verify
		// token and whether Embedded Signup exists here; it is polled while an
		// administrator is wiring Meta up, so "Meta verified your webhook"
		// appears without a reload.
		getSetup: builder.query({
			query: () => '/settings/setup',
			providesTags: ['Settings'],
		}),

		startEmbeddedSignup: builder.mutation({
			query: () => ({ url: '/settings/connect/session', method: 'POST' }),
		}),

		// ⚠ The token and app secret in this body are forwarded once and never
		// kept anywhere in the CRM; a refetch after success shows only what the
		// connection layer reports back.
		connectManual: builder.mutation({
			query: (body) => ({ url: '/settings/connect/manual', method: 'POST', body }),
			invalidatesTags: ['Settings', 'Channel'],
		}),

		// Re-reads the connection layer and writes any local channel row that is
		// missing. For an account connected before the CRM persisted rows at all:
		// without it such an account stays invisible for ever, and reconnecting
		// is the only alternative.
		syncChannels: builder.mutation({
			query: () => ({ url: '/settings/channels/sync', method: 'POST' }),
			invalidatesTags: ['Settings', 'Channel'],
		}),

		// ─── New chat ──────────────────────────────────────────────────
		// One debounced request powers both halves of the dialog: the leads to
		// suggest, and how the number will actually be stored. The preview comes
		// from the server so it cannot disagree with what gets written.
		leadSuggestions: builder.query({
			query: ({ q, region }) => ({
				url: '/lead-suggestions',
				params: { q, ...(region ? { region } : {}) },
			}),
			// A typeahead's results are transient — no tags, so a conversation or
			// assignment mutation does not refetch them.
		}),

		// Whether a thread for this number already exists, asked before the user
		// commits so the dialog can say "this will open your existing chat".
		lookupConversation: builder.query({
			query: ({ to, region, channelPhoneNumberId }) => ({
				url: '/conversations/lookup',
				params: { to, ...(region ? { region } : {}), ...(channelPhoneNumberId ? { channelPhoneNumberId } : {}) },
			}),
		}),

		// ★ THE ENVELOPE IS KEPT, NOT UNWRAPPED.
		// This used to `transformResponse` down to the array, which threw away the
		// total — and a capped list with no total reads as "these are all of them".
		// The picker needs both halves to say "1–20 of 137".
		listAssignableUsers: builder.query({
			query: ({ phoneNumberId, q = '', page = 1, limit }) => ({
				url: `/settings/channels/${phoneNumberId}/assignable-users`,
				params: {
					...(q ? { q } : {}),
					...(page > 1 ? { page } : {}),
					...(limit ? { limit } : {}),
				},
			}),
			providesTags: (result, error, { phoneNumberId }) => [
				{ type: 'Assignment', id: phoneNumberId },
			],
		}),

		// ─── Campaigns ─────────────────────────────────────────────────────
		// ★ EVERY CONTROL IS A MUTATION THAT INVALIDATES BOTH THE ROW AND THE
		// LIST. A campaign is usually watched from two screens at once — the list
		// on one monitor, the dashboard on another — and a pause that moves only
		// the screen it was pressed on is how somebody presses it twice.
		listCampaigns: builder.query({
			query: (params = {}) => ({ url: '/campaigns', params }),
			providesTags: (result) =>
				result?.campaigns
					? [
							...result.campaigns.map((c) => ({ type: 'Campaign', id: c._id })),
							{ type: 'Campaign', id: 'LIST' },
						]
					: [{ type: 'Campaign', id: 'LIST' }],
		}),

		getCampaign: builder.query({
			query: (id) => `/campaigns/${id}`,
			transformResponse: (r) => r?.campaign ?? null,
			providesTags: (result, error, id) => [{ type: 'Campaign', id }],
		}),

		createCampaign: builder.mutation({
			query: (body) => ({ url: '/campaigns', method: 'POST', body }),
			transformResponse: (r) => r?.campaign ?? null,
			invalidatesTags: [{ type: 'Campaign', id: 'LIST' }],
		}),

		updateCampaign: builder.mutation({
			query: ({ id, ...body }) => ({ url: `/campaigns/${id}`, method: 'PATCH', body }),
			transformResponse: (r) => r?.campaign ?? null,
			invalidatesTags: (result, error, { id }) => [{ type: 'Campaign', id }, { type: 'Campaign', id: 'LIST' }],
		}),

		// The funnel. A mutation rather than a query because it is expensive
		// enough that it must only run when the wizard asks, never on a rerender.
		previewCampaign: builder.mutation({
			query: (id) => ({ url: `/campaigns/${id}/preview`, method: 'POST' }),
		}),

		renderSample: builder.mutation({
			query: ({ id, size }) => ({ url: `/campaigns/${id}/render/sample`, method: 'POST', body: { size } }),
		}),

		// One call per control, so the button that failed is the button that
		// reports it.
		submitCampaign: builder.mutation({
			query: (id) => ({ url: `/campaigns/${id}/submit`, method: 'POST' }),
			invalidatesTags: (r, e, id) => [{ type: 'Campaign', id }, { type: 'Campaign', id: 'LIST' }],
		}),
		approveCampaign: builder.mutation({
			query: ({ id, reason }) => ({ url: `/campaigns/${id}/approve`, method: 'POST', body: { reason } }),
			invalidatesTags: (r, e, { id }) => [{ type: 'Campaign', id }, { type: 'Campaign', id: 'LIST' }],
		}),
		rejectCampaign: builder.mutation({
			query: ({ id, reason }) => ({ url: `/campaigns/${id}/reject`, method: 'POST', body: { reason } }),
			invalidatesTags: (r, e, { id }) => [{ type: 'Campaign', id }, { type: 'Campaign', id: 'LIST' }],
		}),
		startCampaign: builder.mutation({
			query: (id) => ({ url: `/campaigns/${id}/start`, method: 'POST' }),
			invalidatesTags: (r, e, id) => [{ type: 'Campaign', id }, { type: 'Campaign', id: 'LIST' }],
		}),
		pauseCampaign: builder.mutation({
			query: ({ id, reason }) => ({ url: `/campaigns/${id}/pause`, method: 'POST', body: { reason } }),
			invalidatesTags: (r, e, { id }) => [{ type: 'Campaign', id }, { type: 'Campaign', id: 'LIST' }],
		}),
		resumeCampaign: builder.mutation({
			query: (id) => ({ url: `/campaigns/${id}/resume`, method: 'POST' }),
			invalidatesTags: (r, e, id) => [{ type: 'Campaign', id }, { type: 'Campaign', id: 'LIST' }],
		}),
		// M6. PAUSED campaigns only, and it moves ONLY the rows that have not
		// been sent — so the stats tag goes too: the day plan on the dashboard
		// is exactly what this changed.
		rescheduleCampaign: builder.mutation({
			query: ({ id, ...schedule }) => ({ url: `/campaigns/${id}/reschedule`, method: 'POST', body: schedule }),
			invalidatesTags: (r, e, { id }) => [
				{ type: 'Campaign', id },
				{ type: 'Campaign', id: 'LIST' },
				{ type: 'Campaign', id: `stats-${id}` },
			],
		}),
		cancelCampaign: builder.mutation({
			query: ({ id, reason }) => ({ url: `/campaigns/${id}/cancel`, method: 'POST', body: { reason } }),
			invalidatesTags: (r, e, { id }) => [{ type: 'Campaign', id }, { type: 'Campaign', id: 'LIST' }],
		}),
		// ─── Saved audience segments (M5) ──────────────────────────────────
		// Tagged on `Campaign` rather than a type of their own: they are only
		// ever read by the wizard, and one more tag type for three endpoints
		// buys nothing.
		listSegments: builder.query({
			query: () => '/segments',
			transformResponse: (r) => r?.segments ?? [],
			providesTags: [{ type: 'Campaign', id: 'SEGMENTS' }],
		}),
		saveSegment: builder.mutation({
			query: (body) => ({ url: '/segments', method: 'POST', body }),
			invalidatesTags: [{ type: 'Campaign', id: 'SEGMENTS' }],
		}),
		deleteSegment: builder.mutation({
			query: (id) => ({ url: `/segments/${id}`, method: 'DELETE' }),
			invalidatesTags: [{ type: 'Campaign', id: 'SEGMENTS' }],
		}),

		// M7. Creates a NEW draft campaign (the next wave) from the contacts the
		// source reached who never replied — so the LIST is what changes, and the
		// source campaign is untouched.
		resendCampaign: builder.mutation({
			query: ({ id, name, variants }) => ({
				url: `/campaigns/${id}/resend`,
				method: 'POST',
				body: { name, variants },
			}),
			invalidatesTags: [{ type: 'Campaign', id: 'LIST' }],
		}),
		duplicateCampaign: builder.mutation({
			query: ({ id, name }) => ({ url: `/campaigns/${id}/duplicate`, method: 'POST', body: { name } }),
			transformResponse: (r) => r?.campaign ?? null,
			invalidatesTags: [{ type: 'Campaign', id: 'LIST' }],
		}),

		// ★ TENANT-WIDE. Invalidates every campaign, because it stopped all of them.
		emergencyStop: builder.mutation({
			query: (reason) => ({ url: '/campaigns/emergency-stop', method: 'POST', body: { reason } }),
			invalidatesTags: ['Campaign', 'CampaignSettings'],
		}),
		emergencyResume: builder.mutation({
			query: () => ({ url: '/campaigns/emergency-resume', method: 'POST' }),
			invalidatesTags: ['Campaign', 'CampaignSettings'],
		}),

		campaignStats: builder.query({
			// `metric: 'cost'` returns the same breakdown split by money as well as
			// by rows (M4), so one request answers "how many" and "how much".
			query: ({ id, by, metric }) => ({
				url: `/campaigns/${id}/stats`,
				params: { ...(by ? { by } : {}), ...(by && metric ? { metric } : {}) },
			}),
			providesTags: (r, e, { id }) => [{ type: 'Campaign', id: `stats-${id}` }],
		}),

		campaignRecipients: builder.query({
			query: ({ id, ...params }) => ({ url: `/campaigns/${id}/recipients`, params }),
			providesTags: (r, e, { id }) => [{ type: 'Campaign', id: `recipients-${id}` }],
		}),

		// ─── Senders (the pool board) ──────────────────────────────────────
		listSenders: builder.query({
			query: () => '/senders',
			transformResponse: (r) => r?.senders ?? r ?? [],
			providesTags: ['Sender'],
		}),
		refreshSenders: builder.mutation({
			query: () => ({ url: '/senders/refresh', method: 'POST' }),
			invalidatesTags: ['Sender', 'Channel'],
		}),
		patchSender: builder.mutation({
			query: ({ phoneNumberId, ...body }) => ({ url: `/senders/${phoneNumberId}`, method: 'PATCH', body }),
			invalidatesTags: ['Sender'],
		}),

		// ─── Templates ─────────────────────────────────────────────────────
		// ★ THE ADMIN CATALOGUE. Requires `template.view`, which lives in the
		// campaign permission group, and takes a channelId the route authorises
		// nothing about — so the inbox uses `listConversationTemplates` below
		// instead of this.
		listMetaWhatsappTemplates: builder.query({
			query: ({ channelId, status, language, category, includeRetired = false }) => ({
				url: '/templates',
				method: 'GET',
				params: cleanParams({
					channelId,
					status,
					language,
					category,
					includeRetired: includeRetired ? 'true' : undefined,
				}),
			}),
			// ★ WITHOUT THIS, `syncTemplates` INVALIDATED NOTHING.
			// It has always declared `invalidatesTags: ['Template']`, but no query
			// provided that tag — so pressing Sync fetched Meta, updated the
			// mirror, and left the screen showing the list from before it.
			providesTags: ['Template'],
			transformResponse: (response) => (Array.isArray(response?.templates) ? response.templates : []),
		}),

		// The approved templates sendable on ONE conversation. The browser names
		// only the thread; the channel and its Meta account are derived server
		// side and never travel back. Authorised by `message.send.template`, the
		// same question the send itself asks, so a template offered here is one
		// the send will accept.
		listConversationTemplates: builder.query({
			query: ({ conversationId, language, category }) => ({
				url: `/conversations/${conversationId}/templates`,
				method: 'GET',
				params: cleanParams({ language, category }),
			}),
			// Tagged per conversation as well as collectively: a sync refreshes
			// every open picker, and switching threads does not reuse another
			// number's catalogue.
			providesTags: (result, error, { conversationId }) => [
				'Template',
				{ type: 'Template', id: conversationId },
			],
			transformResponse: (response) => (Array.isArray(response?.templates) ? response.templates : []),
		}),

		createMetaWhatsappTemplate: builder.mutation({
			query: (body) => ({
				url: '/templates',
				method: 'POST',
				body,
			}),
			// Submitting a template for approval adds a row to the very list the
			// author is looking at.
			invalidatesTags: ['Template'],
		}),
		syncTemplates: builder.mutation({
			query: () => ({ url: '/templates/sync', method: 'POST' }),
			invalidatesTags: ['Template'],
		}),

		// ─── Campaign settings ─────────────────────────────────────────────
		getCampaignSettings: builder.query({
			query: () => '/campaign-settings',
			// ★ THIS ONE KEEPS ITS ENVELOPE, unlike its siblings above, because the
			// response carries three separate things: the tenant's settings, our
			// capacity assumptions, and the read-only city catalogue. Flattening
			// would mix shipped constants into a document that gets PUT back.
			//
			// ⚠ So read `data.settings.defaults`, never `data.defaults`. Without
			// this transform the raw envelope came back and every `data.<field>`
			// read was undefined — the screen showed shipped defaults instead of
			// saved values, and saving sent an empty opt-out keyword list, which
			// the server refuses. The settings screen could not save at all.
			transformResponse: (r) => ({
				settings: r?.settings ?? null,
				capacity: r?.capacity ?? null,
				cityCatalogue: r?.cityCatalogue ?? {},
			}),
			providesTags: ['CampaignSettings'],
		}),
		updateCampaignSettings: builder.mutation({
			query: (body) => ({ url: '/campaign-settings', method: 'PUT', body }),
			invalidatesTags: ['CampaignSettings'],
		}),

		// ─── Suppressions ──────────────────────────────────────────────────
		listSuppressions: builder.query({
			query: (params = {}) => ({ url: '/suppressions', params }),
			providesTags: ['Suppression'],
		}),
		addSuppression: builder.mutation({
			query: (body) => ({ url: '/suppressions', method: 'POST', body }),
			invalidatesTags: ['Suppression'],
		}),
		importSuppressions: builder.mutation({
			query: (body) => ({ url: '/suppressions/import', method: 'POST', body }),
			invalidatesTags: ['Suppression'],
		}),
		// ⚠ The reason travels in the BODY of a DELETE, because the server
		// refuses a removal without one — taking somebody off a do-not-message
		// list is the one action here that can lead to messaging them again.
		removeSuppression: builder.mutation({
			query: ({ waId, reason }) => ({
				url: `/suppressions/${encodeURIComponent(waId)}`,
				method: 'DELETE',
				body: { reason },
			}),
			invalidatesTags: ['Suppression'],
		}),
	}),
});

export const {
	useListChannelsQuery,
	useListEligibleChannelsQuery,
	useListConversationsQuery,
	useGetConversationQuery,
	useListMessagesQuery,
	useLazyListMessagesQuery,
	useSendMessageMutation,
	useStartConversationMutation,
	useAttachLeadConversationMutation,
	useMarkReadMutation,
	useLinkLeadMutation,
	useClaimConversationMutation,
	useListTransferCandidatesQuery,
	useTransferConversationMutation,
	useReleaseConversationMutation,
	useListLeadConversationsQuery,
	useListSettingsChannelsQuery,
	usePatchChannelMutation,
	useListAssignmentsQuery,
	useAssignUserMutation,
	useRevokeUserMutation,
	useGetIntegrationQuery,
	useListAuditQuery,
	useGetSetupQuery,
	useStartEmbeddedSignupMutation,
	useConnectManualMutation,
	useSyncChannelsMutation,
	useListAssignableUsersQuery,
	useLeadSuggestionsQuery,
	useLookupConversationQuery,
	// Campaign engine
	useListCampaignsQuery,
	useGetCampaignQuery,
	useCreateCampaignMutation,
	useUpdateCampaignMutation,
	usePreviewCampaignMutation,
	useRenderSampleMutation,
	useSubmitCampaignMutation,
	useApproveCampaignMutation,
	useRejectCampaignMutation,
	useStartCampaignMutation,
	usePauseCampaignMutation,
	useResumeCampaignMutation,
	useRescheduleCampaignMutation,
	useCancelCampaignMutation,
	useListSegmentsQuery,
	useSaveSegmentMutation,
	useDeleteSegmentMutation,
	useResendCampaignMutation,
	useDuplicateCampaignMutation,
	useEmergencyStopMutation,
	useEmergencyResumeMutation,
	useCampaignStatsQuery,
	useCampaignRecipientsQuery,
	useListSendersQuery,
	useRefreshSendersMutation,
	usePatchSenderMutation,
	useCreateMetaWhatsappTemplateMutation,
	useListMetaWhatsappTemplatesQuery,
	useListConversationTemplatesQuery,
	useSyncTemplatesMutation,
	useGetCampaignSettingsQuery,
	useUpdateCampaignSettingsMutation,
	useListSuppressionsQuery,
	useAddSuppressionMutation,
	useImportSuppressionsMutation,
	useRemoveSuppressionMutation,
} = metaWhatsappApi;
